import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import { getWorkerAnalyses, deleteWorker } from "@/lib/workers";
import { ArrowLeft, Trash, Plus, ArrowRight, FilmStrip, ChartLine, Warning, CheckCircle, Spinner } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const RISK_STYLES = {
  low: { dot: "bg-hazard-safe", text: "text-hazard-safe", label: "LOW" },
  medium: { dot: "bg-hazard-warning", text: "text-hazard-warning", label: "MEDIUM" },
  high: { dot: "bg-hazard-critical", text: "text-hazard-critical", label: "HIGH" },
  critical: { dot: "bg-hazard-critical", text: "text-hazard-critical", label: "CRITICAL" },
};

export default function WorkerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await getWorkerAnalyses(id)); }
    catch (e) { toast.error("Worker not found"); nav("/dashboard/workers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onDelete = async () => {
    if (!window.confirm("Delete this worker? Past analyses will remain but be unlinked.")) return;
    await deleteWorker(id);
    toast.success("Worker removed");
    nav("/dashboard/workers");
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar variant="app" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 font-mono text-xs uppercase tracking-[0.3em] text-ink-500">Loading…</div>
      </div>
    );
  }

  const { worker, analyses, trend } = data;
  const completed = analyses.filter((a) => a.status === "complete" && a.result);
  const avgScore = completed.length ? Math.round(completed.reduce((s, a) => s + (a.result.overall_risk_score || 0), 0) / completed.length) : null;
  const latest = completed[0]?.result?.overall_risk_score ?? null;
  const earliest = completed[completed.length - 1]?.result?.overall_risk_score ?? null;
  const delta = latest != null && earliest != null ? latest - earliest : null;

  const trendData = trend.map((t) => ({
    date: new Date(t.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: t.score,
    title: t.title,
    id: t.id,
  }));

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/dashboard/workers" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900" data-testid="back-to-workers">
            <ArrowLeft size={14} weight="bold" /> Back to roster
          </Link>
          <div className="flex gap-2">
            <Link to={`/dashboard/upload?worker=${worker.id}`}>
              <Button data-testid="worker-new-analysis" className="rounded-none bg-safety hover:bg-safety-hover text-white" size="sm">
                <Plus size={14} weight="bold" className="mr-1" /> New analysis
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={onDelete} data-testid="worker-delete" className="rounded-none text-ink-500 hover:text-hazard-critical">
              <Trash size={14} weight="bold" />
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-1">Worker</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">{worker.name}</h1>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
              {[worker.job_title, worker.department, worker.employee_id && `EMP # ${worker.employee_id}`].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-0 border border-ink-100">
          <Stat label="Total clips" value={analyses.length} icon={<FilmStrip size={16} weight="bold" />} />
          <Stat label="Avg risk" value={avgScore != null ? `${avgScore}` : "—"} icon={<ChartLine size={16} weight="bold" />} border />
          <Stat label="Latest risk" value={latest != null ? latest : "—"} icon={<Warning size={16} weight="bold" className="text-hazard-critical" />} border />
          <Stat
            label="Trend"
            value={delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}`}
            icon={<ChartLine size={16} weight="bold" className={delta == null ? "text-ink-500" : delta < 0 ? "text-hazard-safe" : "text-hazard-critical"} />}
            border
            valueClass={delta == null ? "" : delta < 0 ? "text-hazard-safe" : "text-hazard-critical"}
          />
        </div>

        {/* Trend chart */}
        <div className="mt-8 border border-ink-100">
          <div className="px-5 py-3 border-b border-ink-100 bg-ink-50 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-700">
            Risk score over time · {trendData.length} datapoint{trendData.length === 1 ? "" : "s"}
          </div>
          <div className="p-6">
            {trendData.length < 1 ? (
              <div className="text-center py-12 text-ink-500">Upload an analysis for this worker to start tracking their risk over time.</div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 10, right: 24, left: 0, bottom: 10 }}>
                    <CartesianGrid stroke="#E4E4E7" strokeDasharray="2 4" />
                    <XAxis dataKey="date" stroke="#52525B" fontSize={11} tick={{ fontFamily: "'IBM Plex Mono', monospace" }} />
                    <YAxis stroke="#52525B" domain={[0, 100]} fontSize={11} tick={{ fontFamily: "'IBM Plex Mono', monospace" }} />
                    <Tooltip
                      contentStyle={{ background: "#09090B", border: "1px solid #09090B", color: "#fff", borderRadius: 0, fontSize: 12 }}
                      labelStyle={{ color: "#A1A1AA", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", fontSize: 10, letterSpacing: 1 }}
                      formatter={(v, n, p) => [v, p?.payload?.title || "Score"]}
                    />
                    <ReferenceLine y={70} stroke="#E11D48" strokeDasharray="3 3" label={{ value: "HIGH", fill: "#E11D48", fontSize: 10, fontFamily: "monospace" }} />
                    <ReferenceLine y={40} stroke="#F59E0B" strokeDasharray="3 3" label={{ value: "MED",  fill: "#F59E0B", fontSize: 10, fontFamily: "monospace" }} />
                    <Line type="monotone" dataKey="score" stroke="#FF4D00" strokeWidth={2.5} dot={{ r: 4, fill: "#FF4D00", stroke: "#09090B", strokeWidth: 1 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Analyses list */}
        <div className="mt-8 border border-ink-100">
          <div className="px-5 py-3 border-b border-ink-100 bg-ink-50 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-700">
            Clips · {analyses.length}
          </div>
          {analyses.length === 0 ? (
            <div className="p-10 text-center text-ink-500">No clips for this worker yet.</div>
          ) : (
            <div className="divide-y divide-ink-100">
              {analyses.map((a) => {
                const style = a.result ? RISK_STYLES[a.result.risk_level] : null;
                return (
                  <Link key={a.id} to={`/dashboard/analysis/${a.id}`} className="grid grid-cols-12 gap-4 p-5 items-center hover:bg-ink-50" data-testid={`worker-analysis-${a.id}`}>
                    <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-ink-900 flex items-center justify-center"><FilmStrip size={14} weight="bold" className="text-white" /></div>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-ink-900 truncate">{a.title}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 truncate">{a.industry} · {a.task_type}</div>
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      {a.status === "complete" && style ? (
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 ${style.dot}`} />
                          <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${style.text}`}>{style.label}</span>
                          <span className="font-display font-bold text-ink-900">{a.result.overall_risk_score}/100</span>
                        </div>
                      ) : a.status === "processing" ? (
                        <span className="inline-flex items-center gap-2 px-2 py-1 border border-safety/40 text-safety font-mono text-[10px] uppercase tracking-[0.25em]"><Spinner size={11} weight="bold" className="animate-spin" /> Analyzing</span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-hazard-critical">Failed</span>
                      )}
                    </div>
                    <div className="col-span-4 md:col-span-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
                      {new Date(a.created_at).toLocaleDateString()}
                    </div>
                    <div className="col-span-2 md:col-span-1 text-right">
                      <ArrowRight size={14} weight="bold" className="text-ink-500" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, border, valueClass = "" }) {
  return (
    <div className={`p-5 ${border ? "md:border-l border-ink-100" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{label}</div>
        <div>{icon}</div>
      </div>
      <div className={`mt-3 font-display font-black text-3xl tracking-tighter text-ink-900 ${valueClass}`}>{value}</div>
    </div>
  );
}
